import { useProductEditor } from '../product-editor-context'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Calendar, Clock, AlertTriangle, Check } from 'lucide-react'
import { format, differenceInDays, parseISO } from 'date-fns'

export function ScheduleSection() {
  const { form, setStartDate } = useProductEditor()

  const status = form.watch('status')
  const startDate = form.watch('featured_start_date')
  const endDate = form.watch('featured_end_date')

  // Calculate duration if both dates are set
  const duration = startDate && endDate
    ? differenceInDays(parseISO(endDate), parseISO(startDate))
    : 7

  // Status badge styling
  const statusStyles: Record<string, string> = {
    draft: 'bg-zinc-100 text-zinc-700',
    scheduled: 'bg-blue-100 text-blue-700',
    featured: 'bg-green-100 text-green-700',
    archived: 'bg-purple-100 text-purple-700',
    hidden: 'bg-red-100 text-red-700',
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="size-5" />
          Status & Scheduling
        </CardTitle>
        <CardDescription>
          Control when this product is live and visible
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status */}
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="draft">
                    <div className="flex items-center gap-2">
                      <div className="size-2 rounded-full bg-zinc-400" />
                      Draft
                    </div>
                  </SelectItem>
                  <SelectItem value="scheduled">
                    <div className="flex items-center gap-2">
                      <div className="size-2 rounded-full bg-blue-500" />
                      Scheduled
                    </div>
                  </SelectItem>
                  <SelectItem value="featured">
                    <div className="flex items-center gap-2">
                      <div className="size-2 rounded-full bg-green-500" />
                      Featured (Live)
                    </div>
                  </SelectItem>
                  <SelectItem value="archived">
                    <div className="flex items-center gap-2">
                      <div className="size-2 rounded-full bg-purple-500" />
                      Archived
                    </div>
                  </SelectItem>
                  <SelectItem value="hidden">
                    <div className="flex items-center gap-2">
                      <div className="size-2 rounded-full bg-red-500" />
                      Hidden
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                {status === 'draft' && 'Not visible to customers. Edit freely before scheduling.'}
                {status === 'scheduled' && 'Will automatically go live on the start date.'}
                {status === 'featured' && 'Currently live as This Week\'s Deal.'}
                {status === 'archived' && 'Available in Past Reviews with archive discount.'}
                {status === 'hidden' && 'Completely hidden from all customers.'}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Schedule dates - only show for scheduled/featured */}
        {(status === 'scheduled' || status === 'featured') && (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="featured_start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </FormControl>
                    <FormDescription>
                      When the deal goes live
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="featured_end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription>
                      {duration === 7 ? 'Default: 7 days' : `Duration: ${duration} day${duration !== 1 ? 's' : ''}`}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Schedule Summary */}
            {startDate && endDate && (
              <div className="rounded-lg border bg-zinc-50 p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                  <Clock className="size-4" />
                  Schedule Summary
                </div>
                <div className="grid sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-zinc-500">Starts</div>
                    <div className="font-medium">
                      {format(parseISO(startDate), 'MMM d, yyyy')}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Ends</div>
                    <div className="font-medium">
                      {format(parseISO(endDate), 'MMM d, yyyy')}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Duration</div>
                    <div className="font-medium">
                      {duration} day{duration !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Status info alerts */}
        {status === 'scheduled' && !startDate && (
          <Alert>
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Please set a start date to schedule this product.
            </AlertDescription>
          </Alert>
        )}

        {status === 'featured' && endDate && parseISO(endDate) < new Date() ? (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="size-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              This product's featured period has expired. It is no longer visible as the featured deal.
              Change the status to "Archived" to move it to Past Reviews.
            </AlertDescription>
          </Alert>
        ) : status === 'featured' && (
          <Alert className="border-green-200 bg-green-50">
            <Check className="size-4 text-green-600" />
            <AlertDescription className="text-green-700">
              This product is currently live and visible to customers as the featured deal.
            </AlertDescription>
          </Alert>
        )}

        {/* Current status badge */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-500">Current status:</span>
          <Badge className={statusStyles[status] || statusStyles.draft}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}
